import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, forwardRef, useMemo, useState } from 'react';
import { HierarchicalFolder } from '@/types/hierarchicalFolders';
import { FeedVideoCard } from './FeedVideoCard';
import { DraggableFeedVideoCard } from './DraggableFeedVideoCard';
import { FeedVideoCardSkeleton } from './FeedVideoCardSkeleton';
import { NewVideoCard } from './NewVideoCard';
import { EmptyVideoState } from './EmptyVideoState';
import { RenameVideoModal } from '@/components/shared/RenameVideoModal';
import { DeleteVideoModal } from '@/components/shared/DeleteVideoModal';
import { toast } from 'sonner';
import { SearchX } from 'lucide-react';
import { downloadVideo } from '@/utils/downloadVideo';
import { normalizeText } from '@/utils/normalizeText';
import { videoService } from '@/services/videoService';

interface Video {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  duration: number;
  status: string;
  plays: number;
  views: number;
  createdAt: string;
  videoDownload?: string | null;
  sharedId?: string;
  tracking?: {
    conversions: number;
    revenue: number;
    currency: string;
  };
}

interface DashboardVideoFeedProps {
  searchQuery?: string;
  /**
   * Ordenação client-side da página já carregada.
   * Opcional de propósito: sem ela o feed respeita a ordem que a API devolveu.
   */
  sortOption?: { metric: 'plays' | 'views'; order: 'desc' | 'asc' };
  includeNewVideoCard?: boolean;
  videos?: Video[];
  isLoading?: boolean;
  folders?: HierarchicalFolder[];
  onMoveToFolder?: (videoId: string, folderId: string) => void;
  isInsideFolder?: boolean;
  onRemoveFromFolder?: (videoId: string) => void;
}

export const DashboardVideoFeed = forwardRef<HTMLDivElement, DashboardVideoFeedProps>(
  ({ searchQuery = '', sortOption, includeNewVideoCard = false, videos: videosFromProps = [], isLoading: isLoadingFromProps = false, folders = [], onMoveToFolder, isInsideFolder = false, onRemoveFromFolder }, ref) => {
  useLanguage();
    const [localVideos, setLocalVideos] = useState<Video[]>(videosFromProps);

    // Atualizar vídeos locais quando props mudarem
    useEffect(() => {
      setLocalVideos(videosFromProps);
    }, [videosFromProps]);

    // Filter videos by search query (feito no frontend para melhor UX) + ordenação opcional
    const filteredVideos = useMemo(() => {
      // Busca insensível a acentos: "metricas" encontra "Métricas".
      const term = normalizeText(searchQuery);
      let result = term
        ? localVideos.filter(video => normalizeText(video.title).includes(term))
        : localVideos;

      // Sempre copiar antes de ordenar: `localVideos` aponta para o array do cache
      // do React Query e um sort in-place mutaria esse cache.
      if (sortOption) {
        result = [...result].sort((a, b) => {
          const valA = sortOption.metric === 'plays' ? (a.plays || 0) : (a.views || 0);
          const valB = sortOption.metric === 'plays' ? (b.plays || 0) : (b.views || 0);
          return sortOption.order === 'desc' ? valB - valA : valA - valB;
        });
      }

      return result;
    }, [localVideos, searchQuery, sortOption]);

    // Estados para modais
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [videoToRename, setVideoToRename] = useState<{ id: string; title: string } | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [videoToDelete, setVideoToDelete] = useState<{ id: string; title: string } | null>(null);

    const handleRename = (id: string) => {
      const video = filteredVideos.find(v => v.id === id);
      if (video) {
        setVideoToRename({ id: video.id, title: video.title });
        setRenameModalOpen(true);
      }
    };

    const handleConfirmRename = async (newTitle: string) => {
      if (videoToRename) {
        try {
          await videoService.updateVideo(videoToRename.id, { nomeVideo: newTitle });
          // Atualizar localmente
          setLocalVideos(prev =>
            prev.map(v => v.id === videoToRename.id ? { ...v, title: newTitle } : v)
          );
          toast.success(translateInterfaceText("Vídeo renomeado com sucesso!"));
        } catch (error) {
          toast.error(translateInterfaceText("Erro ao renomear vídeo"));
          console.error('Erro ao renomear:', error);
        }
      }
    };

    const handleDelete = (id: string) => {
      const video = filteredVideos.find(v => v.id === id);
      if (video) {
        setVideoToDelete({ id: video.id, title: video.title });
        // Pequeno delay para garantir que o DropdownMenu fecha primeiro
        setTimeout(() => {
          setDeleteModalOpen(true);
        }, 100);
      }
    };

    const handleConfirmDelete = async () => {
      if (videoToDelete) {
        try {
          await videoService.deleteVideo(videoToDelete.id);
          // Remover localmente
          setLocalVideos(prev => prev.filter(v => v.id !== videoToDelete.id));
          toast.success(translateInterfaceText("Vídeo excluído com sucesso!"));
          // Fechar modal (o onOpenChange irá limpar o estado)
          setDeleteModalOpen(false);
        } catch (error) {
          toast.error(translateInterfaceText("Erro ao excluir vídeo"));
          console.error('Erro ao excluir:', error);
        }
      }
    };

    const handleDownload = async (id: string) => {
      const video = filteredVideos.find(v => v.id === id);
      if (video?.videoDownload) {
        await downloadVideo(video.videoDownload, video.title);
        return;
      }
      // videoDownload não estava na listagem (ex: vídeos dentro de pasta) — busca individualmente
      try {
        const response: any = await videoService.getVideoById(id);
        // A API pode retornar {response: {...}}, {response: [{...}]}, [{...}], {data: {...}} ou o objeto direto
        let videoData: any = null;
        if (response?.response) {
          videoData = Array.isArray(response.response) ? response.response[0] : response.response;
        } else if (Array.isArray(response)) {
          videoData = response[0];
        } else {
          videoData = response?.data || response;
        }
        const downloadUrl = videoData?.videoMP4 || videoData?.videoDownload || videoData?.video_download;
        if (downloadUrl) {
          await downloadVideo(downloadUrl, video?.title || videoData?.nomeVideo || 'video');
        } else {
          toast.error(translateInterfaceText("URL de download não disponível"));
        }
      } catch {
        toast.error(translateInterfaceText("Erro ao obter URL de download"));
      }
    };


    // Empty state - só mostra se não tem vídeos E não está mostrando o NewVideoCard
    if (!isLoadingFromProps && filteredVideos.length === 0 && !includeNewVideoCard) {
      return (
        <div ref={ref}>
          {searchQuery ? (
            <div className="flex flex-col items-center justify-center text-center py-14 px-6 rounded-[12px] border border-dashed border-[#BCD0FA] bg-[#FBFCFE]">
              <div className="w-10 h-10 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center mb-3">
                <SearchX className="w-5 h-5 text-[#3478F6]" strokeWidth={1.8} />
              </div>
              <p className="text-[14px] font-medium text-[#101828]">
                {translateInterfaceText("Nenhum vídeo encontrado para “")}{searchQuery}”
              </p>
              <p className="mt-1 text-[12.5px] text-[#98A2B3]">
                {translateInterfaceText("Tente outro termo ou revise a ortografia.")}</p>
            </div>
          ) : (
            <EmptyVideoState />
          )}
        </div>
      );
    }

    return (
      <div ref={ref} className="space-y-6">
        {/* Video Grid - 4 columns on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-[18px] gap-y-[20px] feed-grid-optimized">
          {/* Card "Novo Vídeo" como primeiro item */}
          {includeNewVideoCard && !isLoadingFromProps && <NewVideoCard />}

          {/* Loading Skeleton */}
          {isLoadingFromProps && (
            <>
              {Array.from({ length: 8 }).map((_, i) => (
                <FeedVideoCardSkeleton key={`skeleton-${i}`} />
              ))}
            </>
          )}

          {/* Video Cards */}
          {!isLoadingFromProps && (() => {
            // O primeiro card não-processando vira o alvo do tour guiado
            let tourTargetAssigned = false;
            return filteredVideos.map((video) => {
              const isSharedVideo = Boolean(video.sharedId && video.sharedId !== '' && video.sharedId !== video.id);
              const isVideoProcessing = !isSharedVideo && (video.status === 'processing' || video.status === 'pending');
              const isTourTarget = !tourTargetAssigned && !isVideoProcessing;
              if (isTourTarget) tourTargetAssigned = true;
              return (
                <DraggableFeedVideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnailUrl || ''}
                  views={video.views || 0}
                  plays={video.plays || 0}
                  disabled={isVideoProcessing}
                >
                  {({ isDragging }) => (
                    <FeedVideoCard
                      id={video.id}
                      title={video.title}
                      thumbnail={video.thumbnailUrl || ''}
                      views={video.views || 0}
                      plays={video.plays || 0}
                      isProcessing={!isSharedVideo && video.status === 'processing'}
                      processingProgress={undefined}
                      status={video.status as any}
                      onDelete={handleDelete}
                      onRename={handleRename}
                      onDownload={handleDownload}
                      folders={folders}
                      onMoveToFolder={onMoveToFolder}
                      isInsideFolder={isInsideFolder}
                      onRemoveFromFolder={onRemoveFromFolder}
                      isSharedVideo={isSharedVideo}
                      isTourTarget={isTourTarget}
                      isDragging={isDragging}
                    />
                  )}
                </DraggableFeedVideoCard>
              );
            });
          })()}
        </div>

        {/* Modal de Renomear */}
        <RenameVideoModal
          open={renameModalOpen}
          onOpenChange={setRenameModalOpen}
          currentTitle={videoToRename?.title || ''}
          onConfirm={handleConfirmRename}
        />

        {/* Modal de Excluir */}
        <DeleteVideoModal
          open={deleteModalOpen}
          onOpenChange={(open) => {
            setDeleteModalOpen(open);
            if (!open) {
              // Limpar estado quando o modal fecha
              setTimeout(() => {
                setVideoToDelete(null);
              }, 100);
            }
          }}
          videoTitle={videoToDelete?.title || ''}
          onConfirm={handleConfirmDelete}
        />
      </div>
    );
  }
);

DashboardVideoFeed.displayName = 'DashboardVideoFeed';
